import axios from 'axios';
import { format } from 'date-fns';

const API_URL = 'https://botestlegal.magnussoftech.in/api/WebServiceForStaffData.aspx';

console.log('API URL:', API_URL);

// Create axios instance with default config
const api = axios.create({
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    'Accept': 'application/json, text/html, */*'
  },
  withCredentials: false,
  timeout: 10000
});

console.log('Axios instance created with config:', {
  headers: api.defaults.headers,
  withCredentials: api.defaults.withCredentials,
  timeout: api.defaults.timeout
});

// Add response interceptor for debugging
api.interceptors.response.use(
  response => {
    console.log('=== RESPONSE INTERCEPTOR START ===');
    const timestamp = new Date().toISOString();
    console.log('Response timestamp:', timestamp);
    console.log('Response status:', response.status);
    console.log('Response status text:', response.statusText);
    console.log('Response headers:', response.headers);
    console.log('Response data type:', typeof response.data);
    console.log('Response data preview:', 
      typeof response.data === 'string' 
        ? response.data.substring(0, 200) + '...' 
        : response.data
    );
    
    // Check if response is HTML
    if (typeof response.data === 'string' && response.data.includes('<!DOCTYPE html>')) {
      console.error('🚨 RECEIVED HTML RESPONSE INSTEAD OF JSON');
      console.log('HTML Response length:', response.data.length);
      console.log('HTML preview:', response.data.substring(0, 500));
      
      // Check if this is an authentication issue
      if (response.data.includes('login') || response.data.includes('Login') || 
          response.data.includes('__VIEWSTATE')) {
        console.log('🔒 Authentication appears to have expired, clearing tokens');
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('token');
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }
      
      // If it's HTML but not a login page, treat as empty response
      console.log('⚠️ Received HTML response but not login page, treating as empty data');
      response.data = [];
      return response;
    }

    // Handle string response
    if (typeof response.data === 'string') {
      console.log('📄 Processing string response');
      try {
        const jsonPart = response.data.split('||JasonEnd')[0].trim();
        console.log('Extracted JSON part length:', jsonPart.length);
        console.log('JSON part preview:', jsonPart.substring(0, 200));
        
        if (jsonPart) {
          const parsedData = JSON.parse(jsonPart);
          console.log('✅ Successfully parsed JSON data');
          console.log('Parsed data structure:', {
            isArray: Array.isArray(parsedData),
            length: parsedData?.length || 'N/A',
            firstItemKeys: parsedData?.[0] ? Object.keys(parsedData[0]) : 'N/A'
          });
          
          if (parsedData[0]?.ErrorCode !== "0") {
            console.error('❌ API Error detected:', parsedData[0]?.ErrorMessage);
            throw new Error(parsedData[0]?.ErrorMessage || 'API Error');
          }
          
          if (parsedData[0]?.JSONData1) {
            console.log('📋 Found JSONData1, attempting to parse');
            try {
              const nestedData = JSON.parse(parsedData[0].JSONData1);
              console.log('✅ Successfully parsed JSONData1');
              console.log('Nested data structure:', {
                isArray: Array.isArray(nestedData),
                length: nestedData?.length || 'N/A'
              });
              response.data = nestedData;
              return response;
            } catch (nestedError) {
              console.error('❌ Error parsing JSONData1:', nestedError);
              response.data = parsedData;
              return response;
            }
          }
          response.data = parsedData;
          return response;
        }
      } catch (error) {
        console.error('❌ Error parsing response:', error);
        throw new Error('Invalid response format from server');
      }
    }
    
    console.log('✅ Response processing completed successfully');
    console.log('=== RESPONSE INTERCEPTOR END ===');
    return response;
  },
  error => {
    console.error('=== RESPONSE ERROR INTERCEPTOR START ===');
    console.error('❌ Network/Response Error occurred');
    console.error('Error message:', error.message);
    console.error('Error code:', error.code);
    
    if (error.response) {
      console.error('Error response details:', {
        status: error.response.status,
        statusText: error.response.statusText,
        headers: error.response.headers,
        dataType: typeof error.response.data,
        dataPreview: typeof error.response.data === 'string' 
          ? error.response.data.substring(0, 200) 
          : error.response.data
      });
      
      if (error.response.status === 401 || 
          (typeof error.response.data === 'string' && error.response.data.includes('<!DOCTYPE html>'))) {
        console.log('🔒 Authentication error detected, clearing tokens and redirecting to login');
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    } else if (error.request) {
      console.error('❌ Network request failed - no response received');
      console.error('Request details:', error.request);
    } else {
      console.error('❌ Request setup error:', error.message);
    }
    
    console.log('=== RESPONSE ERROR INTERCEPTOR END ===');
    return Promise.reject(error);
  }
);

// Helper functions for date formatting
export const formatDateForApi = (dateStr) => {
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date: ${dateStr}`);
    }
    // Convert to .NET JSON date format: /Date(timestamp)/
    const timestamp = date.getTime();
    return `/Date(${timestamp})/`;
  } catch (error) {
    console.error('Error formatting date for API:', error);
    throw new Error(`Invalid date format for API: ${dateStr}`);
  }
};

export const formatDateString = (dateStr) => {
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr; // Return original if invalid
    }
    return format(date, 'dd-MM-yyyy');
  } catch (error) {
    console.error('Error formatting date string:', error);
    return dateStr; // Fallback
  }
};

// API call for fetching employee leaves data list for admin
export const getEmployeeLeavesDataListForAdmin = async (companyCode) => {
  console.log('=== getEmployeeLeavesDataListForAdmin Start ===');
  try {
    const formData = new URLSearchParams();
    formData.append('title', 'GetEmployeeLeavesDataListForAdmin');
    formData.append('ReqCompanyCode', companyCode);

    console.log('Request data:', {
      title: 'GetEmployeeLeavesDataListForAdmin',
      ReqCompanyCode: companyCode
    });

    const response = await api.post(API_URL, formData);
    console.log('Raw API Response:', response);
    
    if (!response.data) {
      console.error('No data received from API');
      return [];
    }

    if (typeof response.data === 'string') {
      console.log('Processing string response');
      const jsonPart = response.data.split('||JasonEnd')[0].trim();
      console.log('JSON part:', jsonPart);
      
      if (jsonPart) {
        const parsedData = JSON.parse(jsonPart);
        console.log('Parsed data:', parsedData);
        
        if (parsedData[0]?.ErrorCode === "0") {
          if (parsedData[0]?.JSONData1) {
            try {
              const nestedData = JSON.parse(parsedData[0].JSONData1);
              console.log('Successfully parsed JSONData1:', nestedData);
              return nestedData;
            } catch (error) {
              console.error('Error parsing JSONData1:', error);
              return [];
            }
          } else {
            console.log('No JSONData1 found in response');
            return [];
          }
        } else {
          console.error('API Error:', parsedData[0]?.ErrorMessage);
          throw new Error(parsedData[0]?.ErrorMessage || 'API Error');
        }
      }
    } else if (Array.isArray(response.data)) {
      console.log('Response is already an array:', response.data);
      return response.data;
    }
    
    console.log('No valid data found in response');
    return [];
  } catch (error) {
    console.error('Error in getEmployeeLeavesDataListForAdmin:', error);
    throw error;
  }
};

// API call for getting employee leave applications
export const getEmployeeLeaveApplications = async (employeeCode, companyCode) => {
  try {
    const formData = new URLSearchParams();
    formData.append('title', 'GetEmployeeLeaveApplications');
    formData.append('ReqEmpCode', employeeCode);
    formData.append('ReqCompanyCode', companyCode);

    const response = await api.post(API_URL, formData);

    if (typeof response.data === 'string') {
      const jsonPart = response.data.split('||JasonEnd')[0].trim();
      const parsed = JSON.parse(jsonPart);
      if (parsed[0]?.JSONData1) {
        return JSON.parse(parsed[0].JSONData1);
      }
    }
    return [];
  } catch (error) {
    console.error('Error fetching employee leave applications:', error);
    throw error;
  }
};

// API call for updating leave application by staff
export const updateLeaveAppStaff = async (employeeCode, mergedData) => {
  try {
    const requestData = {
      ActionType: 2,
      AppDate: mergedData.AppDate,
      AppDateStr: mergedData.AppDateStr,
      Designation: mergedData.Designation || "",
      DrpYN: false,
      EMPAUTOID: mergedData.EMPAUTOID,
      EMPCODE: mergedData.EMPCODE,
      EmployeeName: mergedData.EmployeeName || "",
      EmployeeType: mergedData.EmployeeType || "",
      LVAPPID: mergedData.LVAPPID,
      LVAppRejBy: mergedData.reviewedBy || "",
      LVAppRejDate: mergedData.reviewDate,
      LVAppRejDateStr: mergedData.reviewDateStr,
      LVAppRejReason: mergedData.reviewReason || "",
      LVDateF: mergedData.LVDateF,
      LVDateFStr: mergedData.LVDateFStr,
      LVDateT: mergedData.LVDateT,
      LVDateTStr: mergedData.LVDateTStr,
      LVDays: mergedData.LVDays,
      LVReason: mergedData.LVReason || "",
      LVRemark: mergedData.LVRemark || "",
      LeaveStatus: mergedData.status || "Pending",
      LeavesType: mergedData.leavesType || "Half Leave",
      ORDSTR: null,
      ORGAUTOID: mergedData.ORGAUTOID,
      QRYSTR: null
    };

    const formData = new URLSearchParams();
    formData.append('title', 'UpdateLeaveApp');
    formData.append('ReqJSonData', JSON.stringify([requestData]));

    const response = await api.post(API_URL, formData);

    if (typeof response.data === 'string') {
      const jsonPart = response.data.split('||JasonEnd')[0].trim();
      const parsed = JSON.parse(jsonPart);
      return parsed;
    }
    return response.data;
  } catch (error) {
    console.error('Error updating leave application by staff:', error);
    throw error;
  }
};

// API call for updating leave application
export const updateLeaveApplication = async (leaveData) => {
  try {
    const calculateLeaveDays = (fromDate, toDate) => {
      const start = new Date(fromDate);
      const end = new Date(toDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays + 1; // +1 to include both start and end dates
    };

    const lVDays = calculateLeaveDays(leaveData.leaveFrom, leaveData.leaveTo);

    const requestData = {
      AppDate: formatDateForApi(leaveData.applicationDate),
      LVDateF: formatDateForApi(leaveData.leaveFrom),
      LVDateT: formatDateForApi(leaveData.leaveTo),
      LVReason: leaveData.reason,
      LVRemark: leaveData.remark,
      LeavesType: leaveData.leaveType,
      LVDays: lVDays,
      EMPAUTOID: parseInt(leaveData.empAutoId),
      EMPCODE: leaveData.employeeCode,
      EmployeeName: leaveData.employeeName,
      ORGAUTOID: parseInt(localStorage.getItem('companyCode')) || 1,
      LVAPPID: parseInt(leaveData.leaveAppId) || 0, // 0 for new, existing ID for update
      ActionType: leaveData.leaveAppId ? 2 : 1, // 1 for Add, 2 for Update
    };

    const formData = new URLSearchParams();
    formData.append('title', 'UpdateLeaveApp');
    formData.append('ReqJSonData', JSON.stringify([requestData]));

    const response = await api.post(API_URL, formData);

    if (typeof response.data === 'string') {
      const jsonPart = response.data.split('||JasonEnd')[0].trim();
      const parsed = JSON.parse(jsonPart);
      return parsed;
    }
    return response.data;
  } catch (error) {
    console.error('Error updating leave application:', error);
    throw error;
  }
};

// Helper for mapping status numbers to strings
export const mapLeaveStatusToString = (value) => {
  switch (value?.toString()) {
    case '1':
      return 'Accepted';
    case '2':
      return 'Rejected';
    case '3':
      return 'Hold';
    case '4': // Assuming 4 could be Pending or a default
      return 'Pending';
    default:
      return 'Pending';
  }
};

// Helper for mapping status strings to numbers
export const mapLeaveStatusToNumber = (status) => {
  switch (status) {
    case 'Accepted':
      return 1;
    case 'Rejected':
      return 2;
    case 'Hold':
      return 3;
    case 'Pending':
      return 4;
    default:
      return 4;
  }
};

// API call for getting all leave applications (for admin)
export const getAllLeaveApplications = async () => {
  try {
    const formData = new URLSearchParams();
    formData.append('title', 'GetAllLeaveApplications');
    formData.append('ReqCompanyCode', localStorage.getItem('companyCode'));

    const response = await api.post(API_URL, formData);

    if (typeof response.data === 'string') {
      const jsonPart = response.data.split('||JasonEnd')[0].trim();
      const parsed = JSON.parse(jsonPart);
      if (parsed[0]?.JSONData1) {
        return JSON.parse(parsed[0].JSONData1);
      }
    }
    return [];
  } catch (error) {
    console.error('Error fetching all leave applications:', error);
    throw error;
  }
};

// API call for updating leave application status (for admin) - this looks like the one for review
export const updateLeaveApplicationStatus = async (applicationId, statusData) => {
  try {
    const formData = new URLSearchParams();
    formData.append('title', 'UpdateLeaveApplicationStatus');
    formData.append('ReqLVAPPID', String(applicationId)); // Ensure string conversion
    formData.append('ReqLeaveStatus', statusData.status);
    formData.append('ReqLVAppRejBy', statusData.reviewedBy);
    formData.append('ReqLVAppRejDate', statusData.reviewDate);
    formData.append('ReqLVAppRejReason', statusData.reviewReason);
    formData.append('ReqCompanyCode', localStorage.getItem('companyCode'));

    const response = await api.post(API_URL, formData);

    if (typeof response.data === 'string') {
      const jsonPart = response.data.split('||JasonEnd')[0].trim();
      const parsed = JSON.parse(jsonPart);
      return parsed;
    }
    return response.data;
  } catch (error) {
    console.error('Error updating leave application status:', error);
    throw error;
  }
};

// API call for getting leave application by ID
export const getLeaveApplicationByCode = async (employeeCode, companyCode, leaveAppId) => {
  try {
    const formData = new URLSearchParams();
    formData.append('title', 'GetLeaveApplicationByCode');
    formData.append('ReqEmpCode', employeeCode);
    formData.append('ReqCompanyCode', companyCode);
    formData.append('ReqLVAPPID', String(leaveAppId));

    const response = await api.post(API_URL, formData);

    if (typeof response.data === 'string') {
      const jsonPart = response.data.split('||JasonEnd')[0].trim();
      const parsed = JSON.parse(jsonPart);
      if (parsed[0]?.JSONData1) {
        return JSON.parse(parsed[0].JSONData1);
      }
    }
    return [];
  } catch (error) {
    console.error('Error fetching leave application by code:', error);
    throw error;
  }
};

// API call for updating leave application review
export const updateLeaveApplicationReview = async (reviewData) => {
  const formData = new URLSearchParams();
  formData.append('title', 'UpdateLeaveApplicationStatus');
  formData.append('ReqLVAPPID', String(reviewData.LVAPPID));
  formData.append('ReqLeaveStatus', reviewData.status);
  formData.append('ReqLVAppRejBy', reviewData.reviewedBy);
  formData.append('ReqLVAppRejDate', reviewData.reviewDate);
  formData.append('ReqLVAppRejReason', reviewData.reviewReason);
  formData.append('ReqCompanyCode', localStorage.getItem('companyCode'));

  try {
    const response = await api.post(API_URL, formData);

    if (typeof response.data === 'string') {
      const jsonPart = response.data.split('||JasonEnd')[0].trim();
      const parsed = JSON.parse(jsonPart);
      return parsed; // Return the parsed JSON response
    }
    return response.data; // Fallback if response is not a string
  } catch (error) {
    console.error('Error updating leave application review:', error);
    throw error; // Re-throw for handling in the component
  }
};

// Test backend connectivity
export const testBackendConnectivity = async () => {
  try {
    console.log('🔍 TESTING BACKEND CONNECTIVITY...');
    
    const formData = new URLSearchParams();
    formData.append('title', 'TestConnection');
    formData.append('ReqCompanyCode', '1');
    
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    });
    
    console.log('Backend connectivity test response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      url: response.url
    });
    
    const text = await response.text();
    console.log('Backend response text length:', text.length);
    console.log('Backend response preview:', text.substring(0, 200));
    
    return {
      success: response.ok,
      status: response.status,
      message: response.ok ? 'Backend is reachable' : 'Backend returned error',
      responsePreview: text.substring(0, 500)
    };
  } catch (error) {
    console.error('❌ Backend connectivity test failed:', error);
    return {
      success: false,
      error: error.message,
      message: 'Cannot reach backend server'
    };
  }
};